import AuthorLine from '@/components/AuthorLine';

/**
 * The single card used wherever a story appears in a list — /browse, /search,
 * and the landing page's story section.
 *
 * This markup previously lived inline and identically in BrowseStoriesPage and
 * SearchResultsPage, so any change to the byline, the clamp, or the hover
 * treatment had to be made in every copy or silently drift. Adding a third
 * surface (the landing page) made the duplication untenable.
 *
 * `badge` is the only surface-specific affordance — SearchResultsPage renders
 * a resonance tier there. Everything else is identical on purpose: a story
 * should look the same to a reader no matter which page they met it on.
 */
export default function StoryListCard({ story, onClick, tagOptions, badge = null }) {
  return (
    <div
      className="bg-white rounded-xl border border-[#E6E2D6] p-6 hover:border-[#C05621]/30 transition-all duration-300 hover:shadow-card group cursor-pointer story-card"
      onClick={onClick}
      data-testid={`story-card-${story.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <span className="text-sm font-medium tracking-wide uppercase text-[#6B665E]">
          {story.category}
        </span>
        {badge?.label && (
          <span className={`text-xs px-2 py-1 rounded-full ${badge.className || 'bg-[#E6E2D6] text-[#6B665E]'}`}>
            {badge.label}
          </span>
        )}
      </div>
      <h3 className="text-xl sm:text-2xl md:text-3xl font-serif font-medium text-[#2D2A26] mb-2 group-hover:text-[#C05621] transition-colors break-words">
        {story.title}
      </h3>
      <AuthorLine
        authorName={story.author_name}
        avatarObjectKey={story.author_avatar_object_key}
        professions={story.professions}
        tagOptions={tagOptions}
        className="mb-4"
      />
      <p className="text-base leading-relaxed text-[#6B665E] line-clamp-3">
        {story.preview}
      </p>
      <div className="mt-4 text-[#C05621] text-sm font-medium group-hover:underline">
        Read full story →
      </div>
    </div>
  );
}
